/**
 * Selekt component - the select replacement itself.
 *
 * The original <select> stays in the DOM as the VALUE HOLDER: at any moment it
 * contains only the selected option(s) (rebuilt on every change, then a native
 * 'change' event is dispatched), so forms submit normally and any listener /
 * future validation library keeps working. The full dataset lives in the data
 * layer (data.js), never as <option> nodes.
 *
 * Modes: 'simple' (dropdown; single shows a search over the threshold,
 * multiple is a select2-like tags field), 'complex' (bigger panel: search,
 * results, extra.description pane, Add button), 'switch' (inline segmented
 * control, flat options, single only).
 *
 * Full reference: docs/libs/selekt.md.
 */
import { createSource, flatten, normalizeItems, selectableDescendants } from './data.js';

// Interface strings go through g.translate (identity until the language files
// land); API data is never translated.
const t = (s) => (window.g && window.g.translate ? window.g.translate(s) : s);

let uidCounter = 0;

// The SPECIAL options (filters): a row meaning "the field IS NULL", "the
// field is empty text ''" or "either". In the holder <option> and the
// address each is ONE reserved token (never a lid, never a sane text
// value); on the wire - filterValue(), the lister - it becomes the JSON
// value itself, so NULL and '' stay two different things on every leg.
export const SPECIAL = { null: '~null', empty: '~empty', nullOrEmpty: '~null-or-empty' };
const SPECIAL_WIRE = { '~null': [null], '~empty': [''], '~null-or-empty': [null, ''] };
const SPECIAL_OPTIONS = [['nullOption', SPECIAL.null], ['emptyOption', SPECIAL.empty], ['nullOrEmptyOption', SPECIAL.nullOrEmpty]];

export class Selekt {
	constructor(el, opts) {
		this.el = el;
		this.opts = opts;
		this.uid = `selekt-${++uidCounter}`;
		this.multiple = opts.multiple !== null && opts.multiple !== undefined ? !!opts.multiple : el.multiple;
		if (this.multiple && !el.multiple) el.multiple = true;

		// selection: value(string) -> { item, via } (via = parent value when
		// auto-selected by selectChildren; via entries are hidden when closed
		// and locked in the list).
		this.selection = new Map();
		this.search = '';
		this.page = 1;
		this.more = false;
		this.seq = 0; // request token - stale async responses are dropped
		this.loadedFlat = []; // [{ item, depth }] currently rendered
		this.activeIndex = -1;
		this.open = false;
		// create mode's tags gesture (dunDesk, 0.33.0): the characters that
		// COMMIT the typed text as a tag (and split a pasted list) — an
		// array of single characters, only meaningful with `create`
		this.delimiters = opts.create ? normalizeDelimiters(opts.createDelimiters) : [];
		this.commitQueue = Promise.resolve(); // commits run one after another, never interleaved

		// the special rows this instance offers (a label each, the page's
		// phrase - translated like the placeholder); never in switch mode
		this.specials = opts.mode === 'switch' ? [] : SPECIAL_OPTIONS
			.filter(([key]) => typeof opts[key] === 'string' && opts[key] !== '')
			.map(([key, value]) => ({ value, label: t(opts[key]), disabled: false, extra: { special: true }, children: null }));

		const domItems = this.readDomItems();
		this.source = createSource({
			items: opts.items || domItems,
			endpoint: opts.endpoint || '',
			perPage: opts.perPage,
			getParams: () => this.collectParams(),
		});

		// Initial selection: trust the holder's selected options (value+label).
		// Empty values are placeholders (browsers auto-select the first option
		// of a single select), not real selections.
		Array.from(el.selectedOptions || []).forEach((o) => {
			if (o.value === '') return;
			const special = this.specialItem(o.value);
			this.selection.set(String(o.value), { item: special || { value: o.value, label: o.text, extra: domExtra(o), children: null }, via: null });
		});
		this.initialValues = this.values();

		this.build();
		this.bindDepends();
		this.bindFormReset();
		this.syncHolder(false);
		this.lastFired = this.values().join('');
		el.selekt = this;
	}

	// ---- setup ---------------------------------------------------------------

	// Read the original <option>/<optgroup> tree as the local dataset.
	readDomItems() {
		const parse = (nodes) => Array.from(nodes).map((node) => {
			if (node.tagName === 'OPTGROUP') {
				return { value: `group-${++uidCounter}`, label: node.label, disabled: true, children: parse(node.children) };
			}
			return { value: node.value, label: node.text, disabled: node.disabled, extra: domExtra(node) };
		});
		// a server-rendered special (a preselected token) is not a data row:
		// the lib injects its own
		const items = parse(this.el.children).filter((it) => (it.label !== '' || it.children) && !SPECIAL_WIRE[it.value]);
		return items.length ? items : null;
	}

	collectParams() {
		const params = { ...this.opts.params };
		const dep = this.opts.dependsOn;
		if (dep && dep.source && dep.param) {
			const parent = document.querySelector(dep.source);
			if (parent && parent.value) params[dep.param] = coerce(parent.value);
		}
		return params;
	}

	build() {
		const el = this.el;
		this.wrap = document.createElement('div');
		this.wrap.className = `selekt selekt-${this.multiple ? 'multiple' : 'single'} selekt-mode-${this.opts.mode}`;
		if (this.multiple) this.wrap.classList.add(`selekt-align-${this.opts.controlsAlign === 'bottom' ? 'bottom' : 'top'}`);
		el.parentNode.insertBefore(this.wrap, el);
		this.wrap.appendChild(el);
		el.classList.add('selekt-holder');
		el.tabIndex = -1;
		el.setAttribute('aria-hidden', 'true');

		if (this.opts.mode === 'switch') return this.buildSwitch();
		this.buildControl();
		if (this.opts.mode === 'modal') this.buildModal();
		else this.buildPanel();
	}

	// Closed control: a .form-select so Bootstrap sizing/validation/input-group
	// styling applies untouched. Size + validation classes are copied from the
	// original select.
	buildControl() {
		const toggle = document.createElement('button');
		toggle.type = 'button';
		toggle.className = 'selekt-toggle form-select';
		['form-select-sm', 'form-select-lg', 'is-invalid', 'is-valid'].forEach((c) => {
			if (this.el.classList.contains(c)) toggle.classList.add(c);
		});
		toggle.setAttribute('role', 'combobox');
		toggle.setAttribute('aria-expanded', 'false');
		toggle.setAttribute('aria-haspopup', 'listbox');
		toggle.setAttribute('aria-controls', `${this.uid}-list`);
		if (this.el.disabled) toggle.disabled = true;
		this.toggle = toggle;

		if (this.multiple) {
			this.tagsEl = document.createElement('span');
			this.tagsEl.className = 'selekt-tags';
			this.inline = document.createElement('input');
			this.inline.type = 'text';
			this.inline.size = 1; // otherwise the browser's default size forces wraps
			this.inline.className = 'selekt-inline-input';
			this.inline.setAttribute('autocomplete', 'off');
			this.inline.setAttribute('aria-label', t('Search options'));
			if (this.opts.mode === 'modal') this.inline.readOnly = true; // search lives in the modal
			// The input lives INSIDE the tags container so it flows inline right
			// after the last tag - never onto an (empty-looking) row of its own.
			this.tagsEl.appendChild(this.inline);
			toggle.append(this.tagsEl);
			this.inline.addEventListener('input', () => { this.openPanel(); this.onSearch(this.inline.value); });
			this.inline.addEventListener('keydown', (e) => this.onKeydown(e));
			this.inline.addEventListener('paste', (e) => this.onPaste(e));
			this.inline.addEventListener('focus', () => this.openPanel());
		} else {
			this.valueEl = document.createElement('span');
			this.valueEl.className = 'selekt-value';
			toggle.appendChild(this.valueEl);
		}

		this.clearBtn = document.createElement('span');
		this.clearBtn.className = 'selekt-clear';
		this.clearBtn.setAttribute('role', 'button');
		this.clearBtn.setAttribute('aria-label', t('Clear selection'));
		this.clearBtn.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
		this.clearBtn.addEventListener('click', (e) => { e.stopPropagation(); this.clear(); });
		toggle.appendChild(this.clearBtn);

		toggle.addEventListener('click', () => {
			if (this.multiple) { this.openPanel(); this.inline.focus(); return; }
			this.open ? this.closePanel() : this.openPanel();
		});
		toggle.addEventListener('keydown', (e) => { if (!this.multiple) this.onKeydown(e); });

		this.wrap.appendChild(toggle);
		this.renderClosed();
	}

	// The search input's placeholder: the page's own words, or the standard
	// sentence BY CAPABILITY - with `create` on it says that typing a new
	// value adds it (literal t() calls so the scanner sees both phrases).
	searchPlaceholderText() {
		if (this.opts.searchPlaceholder) return t(this.opts.searchPlaceholder);
		return this.opts.create ? t('Search or add a new value') : t('Search...');
	}

	buildPanel() {
		const p = document.createElement('div');
		p.className = 'selekt-panel';
		p.hidden = true;

		// Panel search: single mode only (multiple searches through the inline
		// input in the control). Simple shows it over the threshold or on ajax;
		// complex always shows it.
		this.panelSearch = null;
		if (!this.multiple) {
			const box = document.createElement('div');
			box.className = 'selekt-panel-search';
			box.innerHTML = '<i class="ri-search-line" aria-hidden="true"></i>';
			this.panelSearch = document.createElement('input');
			this.panelSearch.type = 'text';
			this.panelSearch.className = 'selekt-search-input';
			this.panelSearch.placeholder = this.searchPlaceholderText();
			this.panelSearch.setAttribute('autocomplete', 'off');
			this.panelSearch.setAttribute('aria-label', t('Search options'));
			box.appendChild(this.panelSearch);
			p.appendChild(box);
			this.panelSearch.addEventListener('input', () => this.onSearch(this.panelSearch.value));
			this.panelSearch.addEventListener('keydown', (e) => this.onKeydown(e));
			this.panelSearch.addEventListener('paste', (e) => this.onPaste(e));
			this.searchBox = box;
		}

		p.appendChild(this.buildListBody());
		this.wrap.appendChild(p);
		this.panel = p;

		this.onDocClick = (e) => { if (!this.wrap.contains(e.target)) this.closePanel(); };
	}

	// The results area - shared by the dropdown panel and the modal mode.
	// complex/modal get the two-column layout (list left, side pane right with
	// the Add action + the active item's details), like the workspace modal.
	buildListBody() {
		const body = document.createElement('div');
		body.className = 'selekt-panel-body';
		this.listEl = document.createElement('div');
		this.listEl.className = 'selekt-list';
		this.listEl.id = `${this.uid}-list`;
		this.listEl.setAttribute('role', 'listbox');
		if (this.multiple) this.listEl.setAttribute('aria-multiselectable', 'true');
		body.appendChild(this.listEl);

		this.sideEl = null;
		if (this.opts.mode === 'complex' || this.opts.mode === 'modal') {
			const side = document.createElement('div');
			side.className = 'selekt-side';
			side.innerHTML = `<div class="selekt-side-create" hidden><div class="selekt-side-label"><i class="ri-add-line" aria-hidden="true"></i> ${t('New item')}</div><button type="button" class="btn btn-sm btn-primary selekt-side-add"></button><div class="selekt-side-hint selekt-side-create-hint">${t('Type in the search to add a new option.')}</div></div><div class="selekt-side-info"><div class="selekt-side-label"><i class="ri-information-line" aria-hidden="true"></i> ${t('Details')}</div><div class="selekt-side-title"></div><div class="selekt-side-meta" hidden></div><div class="selekt-side-text"></div><div class="selekt-side-hint selekt-side-info-hint">${t('Hover an option to see its details.')}</div></div>`;
			body.appendChild(side);
			this.sideEl = side;
			this.sideCreate = side.querySelector('.selekt-side-create');
			this.sideAdd = side.querySelector('.selekt-side-add');
			this.sideCreateHint = side.querySelector('.selekt-side-create-hint');
			this.sideTitle = side.querySelector('.selekt-side-title');
			this.sideMeta = side.querySelector('.selekt-side-meta');
			this.sideText = side.querySelector('.selekt-side-text');
			this.sideInfoHint = side.querySelector('.selekt-side-info-hint');
			this.sideAdd.addEventListener('click', () => this.createItem());
		}

		this.listEl.addEventListener('scroll', () => {
			if (!this.more || this.loading) return;
			if (this.listEl.scrollTop + this.listEl.clientHeight >= this.listEl.scrollHeight - 40) this.loadMore();
		});
		this.listEl.addEventListener('click', (e) => {
			const row = e.target.closest('[data-index]');
			if (row) this.activate(parseInt(row.getAttribute('data-index'), 10), true);
			const createRow = e.target.closest('.selekt-create');
			if (createRow) this.createItem();
		});
		// Hovering moves the active row (lightweight - no full re-render), so
		// the description pane follows the mouse too, not just the keyboard.
		this.listEl.addEventListener('mouseover', (e) => {
			const row = e.target.closest('[data-index]');
			if (!row) return;
			const index = parseInt(row.getAttribute('data-index'), 10);
			if (index === this.activeIndex) return;
			const entry = this.loadedFlat[index];
			if (!entry || entry.item.disabled) return;
			this.listEl.querySelectorAll('.is-active').forEach((r) => r.classList.remove('is-active'));
			row.classList.add('is-active');
			this.activeIndex = index;
			this.renderSide();
		});

		return body;
	}

	// Modal mode: closed looks the same, open is a centered modal (like the
	// search modal) with its own search in the header. Mounted on <body> so
	// the backdrop stacks correctly; removed on destroy().
	buildModal() {
		const modal = document.createElement('div');
		modal.className = 'modal fade selekt-modal';
		modal.tabIndex = -1;
		modal.innerHTML = `<div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="selekt-panel-search selekt-modal-head"><i class="ri-search-line" aria-hidden="true"></i><input type="text" class="selekt-search-input" placeholder="${escapeHtml(this.searchPlaceholderText())}" autocomplete="off" aria-label="${t('Search options')}" /><button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('Close')}"></button></div><div class="modal-body"></div></div></div>`;
		document.body.appendChild(modal);
		this.modalEl = modal;
		this.panelSearch = modal.querySelector('.selekt-search-input');
		modal.querySelector('.modal-body').appendChild(this.buildListBody());

		this.panelSearch.addEventListener('input', () => this.onSearch(this.panelSearch.value));
		this.panelSearch.addEventListener('keydown', (e) => this.onKeydown(e));
		this.panelSearch.addEventListener('paste', (e) => this.onPaste(e));
		modal.addEventListener('shown.bs.modal', () => this.panelSearch.focus());
		modal.addEventListener('hidden.bs.modal', () => {
			this.open = false;
			this.wrap.classList.remove('is-open');
			this.toggle.setAttribute('aria-expanded', 'false');
			if (this.multiple) this.inline.value = '';
			this.emit('selekt:close');
		});
	}

	buildSwitch() {
		// the genus segmented control (genus.md "Segmented control"): a pill
		// track, the selected segment a raised thumb — g.segmented slides it
		const group = document.createElement('div');
		// among form controls by default: `.segmented-field` = an input's
		// height and radius (bsp, Sep 2026 - the pill stood 15px taller than
		// the date field beside it); switchStyle 'pill' keeps the standalone look
		const size = this.el.classList.contains('form-select-sm') ? ' segmented-sm' : (this.el.classList.contains('form-select-lg') && this.opts.switchStyle !== 'pill' ? ' segmented-lg' : '');
		group.className = `selekt-switch segmented${this.opts.switchStyle === 'pill' ? '' : ' segmented-field'}${size}`;
		group.setAttribute('role', 'group');
		this.source.load('', 1).then(({ items: loaded }) => {
			const flat = flatten(loaded).map((f) => f.item);
			flat.forEach((item) => {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'segmented-option';
				btn.textContent = item.label;
				btn.disabled = item.disabled || this.el.disabled;
				btn.setAttribute('data-value', item.value);
				if (this.selection.has(String(item.value))) btn.classList.add('active');
				btn.addEventListener('click', () => {
					this.selection.clear();
					this.selection.set(String(item.value), { item, via: null });
					this.syncHolder(true); // repaints the segments
				});
				group.appendChild(btn);
			});
			if (window.g.segmented) window.g.segmented(group);
		});
		this.wrap.appendChild(group);
		this.switchEl = group;
	}

	// ---- open / close ----------------------------------------------------------

	async openPanel() {
		if (this.open || this.el.disabled || this.opts.mode === 'switch') return;
		this.open = true;
		this.wrap.classList.add('is-open');
		this.toggle.setAttribute('aria-expanded', 'true');
		this.emit('selekt:open');

		if (this.opts.mode === 'modal') {
			this.bsModal = window.bootstrap.Modal.getOrCreateInstance(this.modalEl);
			this.panelSearch.value = this.search = '';
			this.bsModal.show();
			await this.load(true);
			return;
		}

		this.panel.hidden = false;
		// Complex panels are wider than the control - flip to right-alignment
		// when the default left-alignment would run off the viewport.
		if (this.opts.mode === 'complex') {
			const rect = this.wrap.getBoundingClientRect();
			const panelWidth = this.panel.offsetWidth;
			const overflowsRight = rect.left + panelWidth > window.innerWidth - 16;
			const fitsFromRight = rect.right - panelWidth >= 16;
			this.panel.classList.toggle('selekt-panel-right', overflowsRight && fitsFromRight);
		}
		// the vertical side, decided BEFORE the first paint of the panel: the
		// worst-case height now (a remote list's real height arrives after the
		// paint - re-deciding then would make the panel jump sides)
		this.placePanel(true);
		document.addEventListener('mousedown', this.onDocClick);

		if (this.panelSearch) {
			this.panelSearch.value = this.search = '';
			// Non-local and complex always search; local simple waits for the
			// first load to know whether it passes the threshold. `create`
			// ALWAYS searches: the search input is the only place a new value
			// can be typed - under the threshold a short local list with
			// `create` on offered the feature and nowhere to use it.
			let show = !this.source.local || this.opts.mode === 'complex' || !!this.opts.create;
			if (!show && this.lastLocalCount !== undefined) show = this.lastLocalCount >= this.opts.searchThreshold;
			this.searchBox.hidden = !show;
			if (show) setTimeout(() => this.panelSearch.focus(), 0);
		}
		await this.load(true);
		if (this.panelSearch && this.source.local && this.opts.mode !== 'complex' && !this.opts.create) {
			const show = this.lastLocalCount >= this.opts.searchThreshold;
			if (show === this.searchBox.hidden) {
				this.searchBox.hidden = !show;
				if (show) setTimeout(() => this.panelSearch.focus(), 0);
			}
		}
		// a LOCAL list rendered within the same task (no paint in between):
		// decide again on the REAL height - three options near the bottom
		// edge fit below and must not flip for a worst case they never reach
		if (this.open && this.source.local) this.placePanel(false);
	}

	// Which side the dropdown opens on (options.drop; 'auto' = by the room).
	// The room is measured against the nearest ancestor that scrolls or clips
	// vertically AND the viewport: inside a scrolling .offcanvas-body or
	// .modal-body the viewport alone sees plenty of space below while the
	// panel lands under the fold of its container (bsp, Sep 2026 - a field
	// on the last row of an offcanvas opened into a 40px sliver). Below when
	// it fits; above when it does not and there is more room there. When
	// neither side fits, the larger side wins and the list is capped to it.
	placePanel(estimate) {
		const panel = this.panel;
		panel.classList.remove('selekt-panel-up');
		this.listEl.style.maxHeight = '';
		const drop = this.opts.drop === 'up' || this.opts.drop === 'down' ? this.opts.drop : 'auto';
		if (drop === 'down') return;
		if (drop === 'up') { panel.classList.add('selekt-panel-up'); return; }
		const GAP = 8;
		let top = 0;
		let bottom = window.innerHeight;
		for (let node = this.wrap.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
			const oy = getComputedStyle(node).overflowY;
			if (oy === 'auto' || oy === 'scroll' || oy === 'hidden' || oy === 'clip') {
				const r = node.getBoundingClientRect();
				top = Math.max(top, r.top);
				bottom = Math.min(bottom, r.bottom);
			}
		}
		const rect = this.wrap.getBoundingClientRect();
		const below = bottom - rect.bottom - GAP;
		const above = rect.top - top - GAP;
		const listMax = parseFloat(getComputedStyle(this.listEl).maxHeight) || 260;
		const chrome = panel.offsetHeight - this.listEl.offsetHeight; // the search row + borders
		const needed = estimate ? chrome + listMax : panel.offsetHeight;
		if (needed <= below) return;
		const up = above > below;
		panel.classList.toggle('selekt-panel-up', up);
		const room = up ? above : below;
		if (needed > room) this.listEl.style.maxHeight = Math.max(120, Math.floor(room - chrome)) + 'px';
	}

	closePanel() {
		if (!this.open) return;
		if (this.opts.mode === 'modal') {
			if (this.bsModal) this.bsModal.hide(); // state resets on hidden.bs.modal
			return;
		}
		this.open = false;
		this.wrap.classList.remove('is-open');
		this.toggle.setAttribute('aria-expanded', 'false');
		this.panel.hidden = true;
		document.removeEventListener('mousedown', this.onDocClick);
		if (this.multiple) { this.inline.value = ''; this.search = ''; }
		this.emit('selekt:close');
	}

	// ---- data ------------------------------------------------------------------

	onSearch(text) {
		this.closeOnEnter = false;
		this.search = text;
		clearTimeout(this.searchTimer);
		const delay = this.source.local ? 0 : this.opts.searchDebounce;
		this.searchTimer = setTimeout(() => this.load(true), delay);
	}

	async load(reset) {
		const token = ++this.seq;
		let added = 0; // rows this page brought (the fill below stops on an empty page)
		if (reset) { this.page = 1; this.loadedFlat = []; this.activeIndex = -1; }
		this.loading = true;
		if (reset) this.renderState('loading');
		try {
			const { items, more } = await this.source.load(this.search.trim(), this.page);
			if (token !== this.seq) return; // stale response - a newer request won
			this.more = more;
			if (this.source.local) this.lastLocalCount = this.search ? this.lastLocalCount : flatten(items).length;
			// the specials lead page 1 only - a remote list's later pages never
			// repeat them - and follow the search by their own label
			const flat = (this.page === 1 ? this.specialRows() : []).concat(flatten(items));
			this.loadedFlat = reset ? flat : this.loadedFlat.concat(flat);
			added = items.length;
			this.renderList();
		} catch (err) {
			if (token !== this.seq) return;
			this.renderState('error', err.message);
		} finally {
			if (token === this.seq) this.loading = false;
		}
		if (token === this.seq && added) this.fillList();
	}

	// A page that does not fill the list leaves nothing to scroll, so the
	// scroll handler that fetches the next page never fires (a small
	// perPage: five rows in a 260px list sat above "Loading more on
	// scroll..." forever). Fetch on until the list overflows or the source
	// runs out - only while the list is laid out: closed or hidden it
	// measures 0 and would pull every page.
	fillList() {
		if (!this.open || !this.more || this.loading) return;
		const list = this.listEl;
		if (!list.clientHeight) return;
		if (list.scrollHeight - list.clientHeight <= 40) this.loadMore();
	}

	loadMore() {
		this.page += 1;
		this.load(false);
	}

	// ---- rendering ---------------------------------------------------------------

	renderState(state, message) {
		if (state === 'loading') this.listEl.innerHTML = `<div class="selekt-msg">${t('Loading...')}</div>`;
		if (state === 'error') this.listEl.innerHTML = `<div class="selekt-msg selekt-msg-error">${escapeHtml(message || 'Error')}</div>`;
	}

	renderList() {
		const rows = [];
		this.loadedFlat.forEach(({ item, depth }, index) => {
			const value = String(item.value);
			const selected = this.selection.has(value);
			const locked = selected && this.selection.get(value).via !== null;
			const classes = ['selekt-item'];
			if (item.disabled) classes.push('is-disabled');
			if (item.disabled && item.children) classes.push('is-group');
			if (item.extra.special) classes.push('is-special');
			if (selected) classes.push('is-selected');
			if (locked) classes.push('is-locked');
			if (index === this.activeIndex) classes.push('is-active');
			const icon = item.extra.icon ? `<i class="${escapeHtml(item.extra.icon)} selekt-item-icon" aria-hidden="true"></i>` : '';
			// extra.meta: the muted secondary text after the label (a
			// parameter's group - names unique only inside it); label and meta
			// share one baseline box, the meta keeps its width, the label
			// ellipsizes first
			const meta = itemMeta(item);
			const label = meta
				? `<span class="selekt-item-text"><span class="selekt-item-label">${escapeHtml(item.label)}</span><span class="selekt-item-meta">${escapeHtml(meta)}</span></span>`
				: `<span class="selekt-item-label">${escapeHtml(item.label)}</span>`;
			rows.push(`<div class="${classes.join(' ')}" role="option" id="${this.uid}-opt-${index}" aria-selected="${selected}" aria-disabled="${item.disabled || locked}" data-index="${index}" style="--selekt-depth:${depth}">${icon}${label}<i class="ri-check-line selekt-item-check" aria-hidden="true"></i></div>`);
			// a hairline between the specials and the data rows
			const next = this.loadedFlat[index + 1];
			if (item.extra.special && next && !next.item.extra.special) rows.push('<div class="selekt-divider" role="separator"></div>');
		});

		if (!rows.length && !this.canCreate()) rows.push(`<div class="selekt-msg">${t('No results')}</div>`);
		// In simple mode the Add action is a row in the list; complex/modal
		// show it in the side pane instead.
		if (this.canCreate() && !this.sideEl) {
			rows.push(`<div class="selekt-create" role="option" id="${this.uid}-opt-create"><i class="ri-add-line" aria-hidden="true"></i> ${t('Add')} «${escapeHtml(this.search.trim())}»</div>`);
		}
		if (this.more) rows.push(`<div class="selekt-msg selekt-more">${t('Loading more on scroll...')}</div>`);
		this.listEl.innerHTML = rows.join('');
		this.renderSide();
	}

	// Side pane (complex/modal): the Add action on top + the active item's
	// title/description below.
	renderSide() {
		if (!this.sideEl) return;
		const createOn = !!this.opts.create;
		this.sideCreate.hidden = !createOn;
		if (createOn) {
			const can = this.canCreate();
			this.sideAdd.hidden = !can;
			if (can) this.sideAdd.textContent = `${t('Add')} «${this.search.trim()}»`;
			this.sideCreateHint.hidden = can;
		}
		const active = this.loadedFlat[this.activeIndex];
		if (active) {
			this.sideTitle.textContent = active.item.label;
			this.sideMeta.textContent = itemMeta(active.item);
			this.sideMeta.hidden = !this.sideMeta.textContent;
			this.sideText.textContent = active.item.extra.description || '';
			this.sideTitle.hidden = false;
			this.sideText.hidden = !active.item.extra.description;
			this.sideInfoHint.hidden = true;
		} else {
			this.sideTitle.hidden = true;
			this.sideMeta.hidden = true;
			this.sideText.hidden = true;
			this.sideInfoHint.hidden = false;
		}
	}

	renderClosed() {
		const visible = Array.from(this.selection.values()).filter((s) => s.via === null);
		this.wrap.classList.toggle('has-value', this.selection.size > 0);
		this.clearBtn.hidden = !(this.opts.clearable && this.selection.size && !this.el.disabled);
		this.wrap.classList.toggle('has-clear', !this.clearBtn.hidden);

		if (!this.multiple) {
			const first = visible[0];
			const meta = first ? itemMeta(first.item) : '';
			this.valueEl.textContent = first ? first.item.label : (this.opts.placeholder ? t(this.opts.placeholder) : t('Select...'));
			if (meta) {
				// the same muted suffix as the option row - two same-named
				// items stay apart in the closed field too
				this.valueEl.textContent = '';
				const labelEl = document.createElement('span');
				labelEl.className = 'selekt-value-label';
				labelEl.textContent = first.item.label;
				const metaEl = document.createElement('span');
				metaEl.className = 'selekt-value-meta';
				metaEl.textContent = meta;
				this.valueEl.append(labelEl, metaEl);
			}
			this.valueEl.classList.toggle('has-meta', !!meta);
			this.valueEl.classList.toggle('is-special', !!(first && first.item.extra.special));
			this.valueEl.classList.toggle('is-placeholder', !first);
			return;
		}

		// maxTagsVisible: false = all tags, 0 = no tags (just "N items
		// selected"), N > 0 = first N tags + "+x more".
		const max = this.opts.maxTagsVisible;
		let shown = visible;
		if (max === 0) shown = [];
		else if (typeof max === 'number' && max > 0) shown = visible.slice(0, max);
		const rest = max === 0 ? 0 : visible.length - shown.length;
		// Rebuild the tags but leave the inline input in place (it is the last
		// child of tagsEl; clearing it too would blur it mid-typing).
		Array.from(this.tagsEl.children).forEach((child) => { if (child !== this.inline) child.remove(); });
		const put = (node) => this.tagsEl.insertBefore(node, this.inline);
		if (max === 0 && visible.length) {
			const count = document.createElement('span');
			count.className = 'selekt-count';
			count.textContent = `${visible.length} ${visible.length === 1 ? t('item selected') : t('items selected')}`;
			put(count);
		}
		shown.forEach(({ item }) => {
			const tag = document.createElement('span');
			tag.className = 'selekt-tag' + (item.children && this.opts.selectChildren ? ' selekt-tag-children' : '') + (item.extra.special ? ' selekt-tag-special' : '');
			const meta = itemMeta(item);
			tag.innerHTML = `<span>${escapeHtml(item.label)}</span>${meta ? `<span class="selekt-tag-meta">${escapeHtml(meta)}</span>` : ''}`;
			const x = document.createElement('button');
			x.type = 'button';
			x.className = 'selekt-tag-x';
			x.setAttribute('aria-label', `${t('Remove')} ${item.label}${meta ? ` (${meta})` : ''}`);
			x.innerHTML = '<i class="ri-close-line" aria-hidden="true"></i>';
			x.addEventListener('click', (e) => { e.stopPropagation(); this.deselect(item.value); });
			tag.appendChild(x);
			put(tag);
		});
		if (rest > 0) {
			const moreTag = document.createElement('span');
			moreTag.className = 'selekt-tag selekt-tag-more';
			moreTag.textContent = `+${rest} ${t('more')}`;
			put(moreTag);
		}
		this.inline.placeholder = visible.length ? '' : (this.opts.placeholder ? t(this.opts.placeholder) : t('Select...'));
	}

	// ---- selection ----------------------------------------------------------------

	activate(index, chose) {
		const entry = this.loadedFlat[index];
		if (!entry) return;
		if (entry.item.disabled) return;
		this.activeIndex = index;
		if (chose) this.choose(entry.item);
		else { this.renderList(); this.scrollActiveIntoView(); }
	}

	choose(item) {
		const value = String(item.value);
		const existing = this.selection.get(value);
		if (existing && existing.via !== null) return; // locked by a selected parent

		if (!this.multiple) {
			this.selection.clear();
			this.selection.set(value, { item, via: null });
			this.syncHolder(true);
			this.closePanel();
			this.toggle.focus();
			return;
		}

		if (existing) {
			this.deselect(value);
			return;
		}
		this.selection.set(value, { item, via: null });
		if (this.opts.selectChildren && item.children) {
			// Absorb ALL selectable descendants under the parent - including any
			// selected explicitly beforehand - so the closed control shows one
			// parent tag, and deselecting the parent cleans the whole subtree.
			selectableDescendants(item).forEach((child) => {
				this.selection.set(String(child.value), { item: child, via: value });
			});
		}
		this.inline.value = '';
		if (this.search) { this.search = ''; this.load(true); }
		this.syncHolder(true);
		this.renderList();
		this.inline.focus();
	}

	deselect(value) {
		value = String(value);
		const entry = this.selection.get(value);
		if (!entry || entry.via !== null) return; // locked children can't be removed directly
		this.selection.delete(value);
		// drop everything auto-selected via this parent
		Array.from(this.selection.entries()).forEach(([v, s]) => {
			if (s.via === value) this.selection.delete(v);
		});
		this.syncHolder(true);
		if (this.open) this.renderList();
	}

	clear() {
		if (!this.selection.size) return;
		this.selection.clear();
		this.syncHolder(true);
		if (this.open) this.renderList();
	}

	canCreate() {
		if (!this.opts.create || !this.search.trim()) return false;
		const needle = this.search.trim().toLowerCase();
		return !this.loadedFlat.some(({ item }) => item.label.toLowerCase() === needle);
	}

	async createItem() {
		const label = this.search.trim();
		// one create at a time (the race-conditions scan): a double-click on
		// the «Add "x"» row posted the endpoint twice before either answered
		// — two items minted server-side; the flag is set synchronously
		if (!label || this.creating) return;
		this.creating = true;
		try {
			let item;
			if (this.opts.create === true) {
				item = normalizeItems([{ value: label, label, extra: { created: true } }])[0];
				this.source.add(item);
			} else {
				// create via endpoint: send label + the same business params
				const res = await window.g.fetch(String(this.opts.create), { label, ...this.collectParams() }, { unguarded: true }); // the `creating` flag is the guard
				if (!res.success) { window.g.error(res.error.message); return; }
				if (res.error && res.error.code) { this.renderState('error', res.error.message); return; }
				this.source.clearCache();
				item = normalizeItems([res.data])[0];
			}

			// Clear the search BEFORE selecting, so choose() doesn't schedule its
			// own (racing) reload for the old search text.
			this.search = '';
			if (this.panelSearch) this.panelSearch.value = '';
			this.choose(item);
			if (!this.multiple) return; // choose() already closed the panel/modal

			// Reload the full list and reveal the freshly added item.
			await this.load(true);
			const index = this.loadedFlat.findIndex((f) => String(f.item.value) === String(item.value));
			if (index >= 0) {
				this.activeIndex = index;
				this.renderList();
				this.scrollActiveIntoView();
			}
			// Modal: one more Enter closes it.
			if (this.opts.mode === 'modal') {
				this.closeOnEnter = true;
				this.panelSearch.focus();
			}
		} finally {
			this.creating = false;
		}
	}

	/**
	 * The tags gesture (createDelimiters): commit ONE typed/pasted piece —
	 * an existing option whose label matches exactly is selected (already
	 * selected = left alone, never toggled off), anything else is created.
	 * The list is loaded for the piece first, so an AJAX source decides
	 * "exists" server-side exactly as typing + Enter would.
	 */
	commit(text) {
		const label = String(text ?? '').trim();
		const run = async () => {
			if (!label) {
				this.setSearchText('');
				return;
			}
			this.setSearchText(label);
			await this.load(true);
			const needle = label.toLowerCase();
			const hit = this.loadedFlat.find(({ item }) => !item.disabled && item.label.toLowerCase() === needle);
			if (hit) {
				this.setSearchText('');
				if (!this.selection.has(String(hit.item.value))) this.choose(hit.item);
				else if (this.multiple) await this.load(true);
				return;
			}
			if (this.opts.create) await this.createItem();
		};
		this.commitQueue = this.commitQueue.then(run, run);
		return this.commitQueue;
	}

	/** Split a pasted list on the delimiters (+ line breaks) and commit the pieces in order. */
	commitAll(text) {
		const pieces = String(text ?? '').split(this.delimiterSplitter()).map((x) => x.trim()).filter(Boolean);
		pieces.forEach((piece) => this.commit(piece));
		return this.commitQueue;
	}

	delimiterSplitter() {
		const chars = this.delimiters.map((d) => d.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')).join('');
		return new RegExp(`[${chars}\\r\\n]+`);
	}

	setSearchText(text) {
		this.search = text;
		if (this.panelSearch) this.panelSearch.value = text;
		if (this.multiple && this.inline) this.inline.value = text;
	}

	onPaste(e) {
		if (!this.delimiters.length) return;
		const text = (e.clipboardData || window.clipboardData)?.getData('text') ?? '';
		if (!this.delimiters.some((d) => text.includes(d)) && !/[\r\n]/.test(text)) return;
		e.preventDefault();
		if (!this.open) this.openPanel();
		this.commitAll(text);
	}

	// ---- holder sync + events --------------------------------------------------------

	values() {
		return Array.from(this.selection.keys());
	}

	syncHolder(fireChange) {
		const el = this.el;
		el.innerHTML = '';
		this.selection.forEach(({ item }, value) => {
			const o = document.createElement('option');
			o.value = value;
			o.textContent = item.label;
			o.selected = true;
			el.appendChild(o);
		});
		// Single with nothing selected: keep an empty option so the field still
		// submits its key (value "") instead of disappearing from the form.
		if (!this.multiple && !this.selection.size) {
			const o = document.createElement('option');
			o.value = '';
			o.selected = true;
			el.appendChild(o);
		}
		if (this.opts.mode !== 'switch') this.renderClosed();
		// the switch paints its state on the segments: EVERY write road
		// (a click, setValue, former.fill, a form reset) lands here, so the
		// repaint lives here too — not only in the click handler (BSP's
		// report: a deep-linked fill held the value on one segment while
		// the old one stayed lit)
		if (this.switchEl) {
			this.switchEl.querySelectorAll('.segmented-option').forEach((b) => b.classList.toggle('active', this.selection.has(String(b.getAttribute('data-value')))));
			if (window.g.segmented) window.g.segmented(this.switchEl); // the thumb follows the repaint
		}
		// Fire only when the value ACTUALLY changed (re-picking the same item
		// must not ripple into listeners / dependent selects).
		const signature = this.values().join('');
		if (fireChange && signature !== this.lastFired) {
			this.lastFired = signature;
			el.dispatchEvent(new Event('change', { bubbles: true }));
			this.emit('selekt:change', { values: this.values() });
		}
	}

	emit(name, detail) {
		this.el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { selekt: this, ...detail } }));
	}

	// ---- keyboard --------------------------------------------------------------------

	onKeydown(e) {
		if (e.key !== 'Enter') this.closeOnEnter = false;
		if (e.key === 'Backspace' && this.multiple && this.inline.value === '') {
			const visible = Array.from(this.selection.values()).filter((s) => s.via === null);
			const last = visible[visible.length - 1];
			if (last) { this.deselect(last.item.value); return; }
		}
		// the tags gesture: a delimiter character commits what is typed
		// (the comma a person's hands type when listing eight room numbers)
		if (this.delimiters.length && e.target !== this.toggle && this.delimiters.includes(e.key)) {
			e.preventDefault();
			if (!this.open) this.openPanel();
			this.commit(e.target.value);
			return;
		}
		if (!this.open && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key) && e.target === this.toggle) {
			e.preventDefault();
			this.openPanel();
			return;
		}
		if (!this.open) return;
		if (e.key === 'Escape') { this.closePanel(); this.toggle.focus(); return; }
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			const dir = e.key === 'ArrowDown' ? 1 : -1;
			let i = this.activeIndex;
			for (let step = 0; step < this.loadedFlat.length; step++) {
				i += dir;
				if (i < 0 || i >= this.loadedFlat.length) break;
				if (!this.loadedFlat[i].item.disabled) { this.activate(i, false); break; }
			}
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (this.closeOnEnter) {
				this.closeOnEnter = false;
				this.closePanel();
				return;
			}
			if (this.activeIndex >= 0 && this.loadedFlat[this.activeIndex]) this.activate(this.activeIndex, true);
			else if (this.canCreate()) this.createItem();
		}
	}

	scrollActiveIntoView() {
		const row = this.listEl.querySelector('.is-active');
		if (row) row.scrollIntoView({ block: 'nearest' });
	}

	// ---- dependsOn ----------------------------------------------------------------------

	bindDepends() {
		const dep = this.opts.dependsOn;
		if (!dep || !dep.source || !dep.param) return;
		const parent = document.querySelector(dep.source);
		if (!parent) return;
		this.updateDependState(parent);
		this.onParentChange = async () => {
			// same generation counter as setValue: a stale lookup resolving
			// after a newer selection write must not merge into it
			const mine = this.valueSeq = (this.valueSeq || 0) + 1;
			this.source.clearCache();
			const values = this.values();
			if (this.opts.keepValue && values.length && parent.value) {
				const real = values.filter((v) => !this.specialItem(v));
				const found = real.length ? await this.source.lookup(real.map(coerce)) : [];
				if (mine !== this.valueSeq) return; // a newer write owns the selection
				const keep = new Set(found.map((f) => String(f.value)));
				values.forEach((v) => { if (this.specialItem(v)) keep.add(v); }); // a special never leaves with the parent
				let changed = false;
				values.forEach((v) => { if (!keep.has(v)) { this.selection.delete(v); changed = true; } });
				if (changed || this.selection.size !== values.length) this.syncHolder(true);
				else this.renderClosed();
			} else if (values.length) {
				this.selection.clear();
				this.syncHolder(true);
			}
			this.updateDependState(parent);
			if (this.open) this.load(true);
		};
		parent.addEventListener('change', this.onParentChange);
		this.dependParent = parent;
	}

	updateDependState(parent) {
		const enabled = !!parent.value && !this.el.disabled;
		if (this.toggle) this.toggle.disabled = !enabled;
		this.wrap.classList.toggle('is-waiting-parent', !enabled);
	}

	// ---- misc public API -------------------------------------------------------------------

	getValue() {
		const v = this.values().map(coerce);
		return this.multiple ? v : (v[0] !== undefined ? v[0] : null);
	}

	// Accepts a scalar, an array, or {value,label} object(s). Unknown labels
	// are resolved through source.lookup (the data.ids convention).
	async setValue(value) {
		// generation counter (the race-conditions scan): a setValue whose
		// network lookup resolves AFTER a newer setValue/parent-change used
		// to add its items ON TOP of the fresh selection — the holder ended
		// as the union and the late change event fired after the owner's
		// guards dropped (phantom filter values). Stale continuations are
		// discarded whole.
		const mine = this.valueSeq = (this.valueSeq || 0) + 1;
		const list = (Array.isArray(value) ? value : [value]).filter((v) => v !== null && v !== undefined && v !== '');
		this.selection.clear();
		const toResolve = [];
		list.forEach((v) => {
			const special = this.specialItem(typeof v === 'object' ? v.value : v);
			// a special resolves without a lookup (the endpoint's data.ids may be lid-typed)
			if (special) this.selection.set(special.value, { item: special, via: null });
			else if (typeof v === 'object') this.selection.set(String(v.value), { item: normalizeItems([v])[0], via: null });
			else toResolve.push(v);
		});
		if (toResolve.length) {
			const found = await this.source.lookup(toResolve);
			if (mine !== this.valueSeq) return; // a newer write owns the selection
			found.forEach((item) => this.selection.set(String(item.value), { item, via: null }));
			toResolve.forEach((v) => {
				if (!this.selection.has(String(v))) this.selection.set(String(v), { item: { value: v, label: String(v), extra: {}, children: null }, via: null });
			});
		}
		this.syncHolder(true);
	}

	// ---- the special options (filters) ----------------------------------------------

	/** Does this instance offer a NULL / empty-text row? */
	hasSpecials() {
		return this.specials.length > 0;
	}

	/** The special item for a token, or null. */
	specialItem(value) {
		return this.specials.find((s) => s.value === String(value)) || null;
	}

	/** The specials that lead a page-1 list: all of them, or those whose label holds the search. */
	specialRows() {
		const needle = this.search.trim().toLowerCase();
		return this.specials.filter((s) => !needle || s.label.toLowerCase().includes(needle)).map((item) => ({ item, depth: 0 }));
	}

	/**
	 * The selection as a FILTER sends it: always an array (single mode too),
	 * the specials mapped to null / '' (deduplicated), the real values as the
	 * holder's strings; null when nothing is picked.
	 */
	filterValue() {
		const out = [];
		this.values().forEach((v) => {
			const wire = this.specialItem(v) ? SPECIAL_WIRE[v] : [v];
			wire.forEach((w) => { if (!out.includes(w)) out.push(w); });
		});
		return out.length ? out : null;
	}

	refresh() {
		this.source.clearCache();
		if (this.open) this.load(true);
	}

	bindFormReset() {
		const form = this.el.closest('form');
		if (!form) return;
		this.onFormReset = () => setTimeout(() => this.setValue(this.initialValues), 0);
		form.addEventListener('reset', this.onFormReset);
	}

	destroy() {
		document.removeEventListener('mousedown', this.onDocClick || (() => {}));
		if (this.bsModal) this.bsModal.dispose();
		if (this.modalEl) this.modalEl.remove();
		if (this.dependParent) this.dependParent.removeEventListener('change', this.onParentChange);
		const form = this.el.closest('form');
		if (form && this.onFormReset) form.removeEventListener('reset', this.onFormReset);
		this.wrap.parentNode.insertBefore(this.el, this.wrap);
		this.wrap.remove();
		this.el.classList.remove('selekt-holder');
		this.el.removeAttribute('aria-hidden');
		this.el.tabIndex = 0;
		delete this.el.selekt;
	}
}

/** extra.meta as a display string ('' = none): a short secondary text, never HTML. */
function itemMeta(item) {
	const meta = item && item.extra ? item.extra.meta : null;
	return meta === null || meta === undefined ? '' : String(meta).trim();
}

/** An <option>'s data-meta attribute -> the item's extra (the static-HTML road to extra.meta). */
function domExtra(option) {
	const meta = option.getAttribute('data-meta');
	return meta ? { meta } : {};
}

function escapeHtml(s) {
	return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

/** createDelimiters: a string of characters or an array of them -> unique single characters. */
function normalizeDelimiters(raw) {
	if (raw === null || raw === undefined || raw === false || raw === true) return [];
	const list = Array.isArray(raw) ? raw : String(raw).split('');
	return Array.from(new Set(list.map((d) => String(d)).filter((d) => d.length === 1)));
}

export function coerce(v) {
	if (typeof v !== 'string') return v;
	if (v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
}
